import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreateNode, useUpdateNode, useSubmitForReview } from '@/hooks/useNodes';
import { useDisciplines } from '@/hooks/useDisciplines';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { Tables } from '@/integrations/supabase/types';

type Node = Tables<'nodes'>;
type NodeType = 'article' | 'lesson' | 'concept' | 'reference';
type NodeStatus = 'draft' | 'in_review' | 'published' | 'archived';

const nodeSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title too long'),
  content: z.string().min(1, 'Content is required'),
  node_type: z.enum(['article', 'lesson', 'concept', 'reference']),
  discipline_id: z.string().min(1, 'Please select a discipline'),
  status: z.enum(['draft', 'in_review', 'published', 'archived']),
});

type NodeFormData = z.infer<typeof nodeSchema>;

interface NodeEditorProps {
  node?: Node;
  onSave?: (node: Node) => void;
  onCancel?: () => void;
}

const NodeEditor: React.FC<NodeEditorProps> = ({ node, onSave, onCancel }) => {
  const { data: disciplines } = useDisciplines();
  const createNodeMutation = useCreateNode();
  const updateNodeMutation = useUpdateNode();
  const submitForReviewMutation = useSubmitForReview();
  const { canEditContent } = useRoleAccess();
  const { user } = useAuth();

  const form = useForm<NodeFormData>({
    resolver: zodResolver(nodeSchema),
    defaultValues: {
      title: node?.title || '',
      content: typeof node?.content === 'object' && node.content && 'text' in node.content 
        ? String(node.content.text) 
        : '',
      node_type: (node?.node_type as NodeType) || 'article',
      discipline_id: node?.discipline_id || '',
      status: (node?.status as NodeStatus) || 'draft',
    },
  });

  const handleSave = async (data: NodeFormData) => {
    try {
      // Security checks
      if (node && node.author_id !== user?.id && !canEditContent) {
        toast.error('You do not have permission to edit this content');
        return;
      }

      // Validate required fields
      if (!data.title.trim()) {
        toast.error('Title is required');
        return;
      }

      if (!data.content.trim()) {
        toast.error('Content is required');
        return;
      }

      if (!data.discipline_id) {
        toast.error('Please select a discipline');
        return;
      }

      // Status validation - only editors can set certain statuses
      if (!canEditContent && ['published', 'in_review'].includes(data.status)) {
        // Contributors can only save as draft
        data.status = 'draft';
      }

      const nodeData = {
        title: data.title.trim(),
        content: {
          text: data.content,
          preview: data.content.substring(0, 200) + (data.content.length > 200 ? '...' : ''),
        },
        node_type: data.node_type,
        discipline_id: data.discipline_id,
        status: data.status,
      };

      if (node) {
        console.log('Updating existing node:', { id: node.id, updates: nodeData });
        const updatedNode = await updateNodeMutation.mutateAsync({
          id: node.id,
          updates: nodeData,
        });
        console.log('Node updated successfully:', updatedNode);
        toast.success('Content updated successfully');
        onSave?.(updatedNode);
      } else {
        console.log('Creating new node:', nodeData);
        const newNode = await createNodeMutation.mutateAsync(nodeData);
        console.log('Node created successfully:', newNode);
        toast.success('Content created successfully');
        onSave?.(newNode);
      }
    } catch (error) {
      console.error('Error saving node:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save content';
      toast.error(errorMessage);
    }
  };

  const handleSubmitForReview = async (data: NodeFormData) => {
    try {
      // Validate required fields before submission
      if (!data.title.trim()) {
        toast.error('Title is required before submitting for review');
        return;
      }

      if (!data.content.trim()) {
        toast.error('Content is required before submitting for review');
        return;
      }

      if (!data.discipline_id) {
        toast.error('Please select a discipline before submitting for review');
        return;
      }

      // If it's a new node, create it first
      if (!node) {
        const nodeData = {
          title: data.title.trim(),
          content: {
            text: data.content,
            preview: data.content.substring(0, 200) + (data.content.length > 200 ? '...' : ''),
          },
          node_type: data.node_type,
          discipline_id: data.discipline_id,
          status: 'draft' as NodeStatus,
        };

        console.log('Creating new node before submission:', nodeData);
        const newNode = await createNodeMutation.mutateAsync(nodeData);
        console.log('New node created:', newNode.id);
        
        // Then submit for review
        await submitForReviewMutation.mutateAsync({ nodeId: newNode.id });
        toast.success('Content created and submitted for review successfully');
        onSave?.(newNode);
      } else {
        // For existing nodes, check if it's eligible for submission
        if (node.status !== 'draft') {
          toast.error(`Cannot submit for review: content is currently ${node.status}. Only draft content can be submitted.`);
          return;
        }

        // Save changes first if needed
        if (form.formState.isDirty) {
          console.log('Saving changes before submission');
          const nodeData = {
            title: data.title.trim(),
            content: {
              text: data.content,
              preview: data.content.substring(0, 200) + (data.content.length > 200 ? '...' : ''),
            },
            node_type: data.node_type,
            discipline_id: data.discipline_id,
            status: 'draft' as const,
          };

          await updateNodeMutation.mutateAsync({
            id: node.id,
            updates: nodeData,
          });
        }
        
        // Then submit for review
        console.log('Submitting existing node for review:', node.id);
        await submitForReviewMutation.mutateAsync({ nodeId: node.id });
        toast.success('Content submitted for review successfully');
      }
    } catch (error) {
      console.error('Error submitting for review:', error);
      
      // Provide specific error messages based on the error type
      if (error instanceof Error) {
        if (error.message.includes('Node not found')) {
          toast.error('Content not found. Please refresh the page and try again.');
        } else if (error.message.includes('Only draft content can be submitted')) {
          toast.error('This content has already been submitted or published. Only draft content can be submitted for review.');
        } else if (error.message.includes('fallback')) {
          toast.success('Content submitted for review (system operating in fallback mode)');
        } else {
          toast.error(`Failed to submit for review: ${error.message}`);
        }
      } else {
        toast.error('Failed to submit content for review. Please try again.');
      }
    }
  };

  const isLoading = createNodeMutation.isPending || updateNodeMutation.isPending || submitForReviewMutation.isPending;
  const canSubmitForReview = !node || (node.status === 'draft');

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>{node ? 'Edit Content' : 'Create New Content'}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form className="space-y-6">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter content title..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="node_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="article">Article</SelectItem>
                        <SelectItem value="lesson">Lesson</SelectItem>
                        <SelectItem value="concept">Concept</SelectItem>
                        <SelectItem value="reference">Reference</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="discipline_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Discipline</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select discipline" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {disciplines?.map((discipline) => (
                          <SelectItem key={discipline.id} value={discipline.id}>
                            {discipline.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {canEditContent && (
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="in_review">In Review</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Write your content here..."
                      className="min-h-[300px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-2">
              <Button
                type="button"
                onClick={form.handleSubmit(handleSave)}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save as Draft'}
              </Button>
              
              {!canEditContent && canSubmitForReview && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={form.handleSubmit(handleSubmitForReview)}
                  disabled={isLoading}
                >
                  {isLoading ? 'Submitting...' : 'Submit for Review'}
                </Button>
              )}

              {onCancel && (
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default NodeEditor;
